import Cookies from "js-cookie";
import { create } from "zustand";
import { API_BASE_URL, AUTH_AUDIENCE } from "@/shared/config/env";
import { http } from "@/shared/api/httpClient";
import { jwtRefreshMarginMs, parseJwtTimes, safeDecodeJwtPayload } from "./jwt";

const TOKEN_KEY = "token";
const COOKIE_KEY = "jwt";

export type AuthState = {
  token: string | null;
  tokenExpirationTime: number | null;
  tokenIssuedAt: number | null;
  tokenIssuer: string | null;
  tokenSubject: string | null;
  displayName: string | null;
  roles: string[];
  setToken: (token: string | null) => void;
  clear: () => void;
  isAuthenticated: () => boolean;
  includeAnyRole: (roles: string | string[]) => boolean;
  authenticate: (username: string, password: string) => Promise<void>;
  logout: () => void;
  initAuth: () => Promise<void>;
  refreshToken: (token?: string) => Promise<void>;
  generateRecover: (username: string) => Promise<unknown>;
  validateRecover: (username: string, code: string) => Promise<unknown>;
  changeByRecover: (username: string, code: string, password: string) => Promise<unknown>;
};

function stripClientCredentials() {
  Cookies.remove(COOKIE_KEY, { sameSite: "Strict", secure: true });
  localStorage.removeItem(TOKEN_KEY);
}

function tokenToState(token: string | null): Pick<
  AuthState,
  | "token"
  | "tokenExpirationTime"
  | "tokenIssuedAt"
  | "tokenIssuer"
  | "tokenSubject"
  | "displayName"
  | "roles"
> {
  if (!token) {
    return {
      token: null,
      tokenExpirationTime: null,
      tokenIssuedAt: null,
      tokenIssuer: null,
      tokenSubject: null,
      displayName: null,
      roles: [],
    };
  }
  const payload = safeDecodeJwtPayload(token);
  if (!payload?.exp) {
    return {
      token: null,
      tokenExpirationTime: null,
      tokenIssuedAt: null,
      tokenIssuer: null,
      tokenSubject: null,
      displayName: null,
      roles: [],
    };
  }
  return {
    token,
    tokenExpirationTime: payload.exp * 1000,
    tokenIssuedAt: payload.iat != null ? payload.iat * 1000 : null,
    tokenIssuer: payload.iss ?? null,
    tokenSubject: payload.sub ?? null,
    displayName: payload.displayName ?? null,
    roles: payload.roles ?? [],
  };
}

function persistToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
  Cookies.set(COOKIE_KEY, token, { sameSite: "Strict", secure: window.location.protocol === "https:" });
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  tokenExpirationTime: null,
  tokenIssuedAt: null,
  tokenIssuer: null,
  tokenSubject: null,
  displayName: null,
  roles: [],

  setToken: (token) => set(tokenToState(token)),

  clear: () => {
    stripClientCredentials();
    set(tokenToState(null));
  },

  isAuthenticated: () => get().token != null,

  includeAnyRole: (roles) => {
    const userRoles = get().roles;
    const list = typeof roles === "string" ? [roles] : roles;
    return list.some((r) => userRoles.includes(r));
  },

  authenticate: async (username, password) => {
    const { data } = await http.post<{ token?: string }>(`${API_BASE_URL}/authenticate`, {
      username,
      password,
      audience: AUTH_AUDIENCE,
    });
    if (!data?.token) return;
    get().setToken(data.token);
    if (!get().isAuthenticated()) {
      get().clear();
      return;
    }
    persistToken(data.token);
  },

  logout: () => {
    get().clear();
  },

  initAuth: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      get().clear();
      return;
    }
    const times = parseJwtTimes(token);
    if (!times) {
      get().clear();
      return;
    }
    const { expMs, iatMs } = times;
    const now = Date.now();
    const margin = jwtRefreshMarginMs(expMs, iatMs);
    if (expMs == null || now >= expMs - margin) {
      await get().refreshToken(token);
      return;
    }
    get().setToken(token);
  },

  refreshToken: async (inputToken) => {
    const token = inputToken ?? get().token;
    if (!token) {
      get().clear();
      throw new Error("no token");
    }
    const times = parseJwtTimes(token);
    if (!times) {
      get().logout();
      throw new Error("invalid jwt");
    }
    const now = Date.now();
    const margin = jwtRefreshMarginMs(times.expMs, times.iatMs);
    if (times.expMs != null && now < times.expMs - margin) {
      return;
    }
    try {
      const { data } = await http.get<{ token?: string }>(`${API_BASE_URL}/refreshtoken`, {
        headers: {
          isRefreshToken: "true",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!data?.token) {
        get().logout();
        throw new Error("refresh failed");
      }
      get().setToken(data.token);
      if (!get().isAuthenticated()) {
        get().clear();
        return;
      }
      persistToken(data.token);
    } catch {
      get().logout();
      throw new Error("refresh failed");
    }
  },

  generateRecover: async (username) => {
    const body = new FormData();
    body.append("username", username);
    const { data } = await http.post(`${API_BASE_URL}/generateRecover`, body);
    return data;
  },

  validateRecover: async (username, code) => {
    const body = new FormData();
    body.append("username", username);
    body.append("code", code);
    const { data } = await http.post(`${API_BASE_URL}/validateRecover`, body);
    return data;
  },

  changeByRecover: async (username, code, password) => {
    const body = new FormData();
    body.append("username", username);
    body.append("code", code);
    body.append("password", password);
    const { data } = await http.post(`${API_BASE_URL}/changeByRecover`, body);
    return data;
  },
}));
